import { useEffect, useRef, useState } from 'react'

// Z34/Z50.3: a native horizontal scrollbar on a tall table sits at the table's own bottom
// edge -- unreachable without first scrolling the whole page down past the table.
// `position: sticky` does NOT fix this: placed after a tall table it only starts "sticking"
// once the viewport is already nearly at the bottom of the page, since sticky measures from
// the element's own static-flow position, not the viewport. The working fix (proven on the
// Jobs list, Z34) is a thin scrollbar mirrored via `position: fixed` to the viewport bottom,
// kept in sync with the real table wrapper's scrollLeft in both directions.
//
// Usage: spread `scrollProps` onto the scrollable wrapper div, `contentRef` onto the table
// (or widest child) inside it, and render <StickyHScrollbar {...bar} /> once, anywhere in
// the page (it's fixed-positioned so placement in the tree doesn't matter).
export function useStickyHScroll(deps: React.DependencyList) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLElement>(null)
  const mirrorRef = useRef<HTMLDivElement>(null)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [hasHScroll, setHasHScroll] = useState(false)

  // Root cause of "scrollbar gone after F5, back after changing Columns": this used to attach its
  // observer once per `deps` change, and bail out (no observer at all) if the table wasn't in the DOM
  // yet -- on first load the page shows a "Loading…" placeholder, and `deps` (rows/columns) often
  // stop changing before the table actually replaces it, so nothing ever re-ran. Now the observer
  // follows the ELEMENTS: every render we check whether the wrapper/table refs changed (placeholder ->
  // real table, tab switch...) and re-attach + re-measure if so. ResizeObserver then handles size
  // changes (data arriving, column changes, fonts), coalesced to one measure per frame; window resize
  // is covered too since the wrapper's width follows the viewport.
  const attached = useRef<{ wrapper: HTMLDivElement | null; content: HTMLElement | null }>({ wrapper: null, content: null })
  const roRef = useRef<ResizeObserver | null>(null)
  const rafRef = useRef(0)
  const measureRef = useRef<() => void>(() => {})

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const wrapper = scrollRef.current
    const content = contentRef.current
    if (attached.current.wrapper === wrapper && attached.current.content === content) return
    attached.current = { wrapper, content }
    roRef.current?.disconnect()
    roRef.current = null
    if (!wrapper || !content) { measureRef.current = () => {}; setHasHScroll(false); return }
    const measure = () => {
      setScrollWidth(wrapper.scrollWidth)
      setHasHScroll(wrapper.scrollWidth > wrapper.clientWidth + 1)
    }
    measureRef.current = measure
    const schedule = () => {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(measure)
    }
    measure()
    const ro = new ResizeObserver(schedule)
    ro.observe(wrapper)
    ro.observe(content)
    roRef.current = ro
    // Guarded by the early return above (only runs when the elements actually changed), so the
    // every-render effect can't loop.
  })

  // Window resize + a re-measure whenever the caller's data/columns change.
  useEffect(() => {
    const onResize = () => measureRef.current()
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      roRef.current?.disconnect()
      roRef.current = null
      attached.current = { wrapper: null, content: null }
      cancelAnimationFrame(rafRef.current)
    }
  }, [])
  useEffect(() => { measureRef.current() },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps)

  const handleScroll = () => {
    if (mirrorRef.current && scrollRef.current) {
      mirrorRef.current.scrollLeft = scrollRef.current.scrollLeft
    }
  }
  const handleMirrorScroll = () => {
    if (scrollRef.current && mirrorRef.current) {
      scrollRef.current.scrollLeft = mirrorRef.current.scrollLeft
    }
  }

  return {
    scrollProps: { ref: scrollRef, onScroll: handleScroll },
    contentRef,
    bar: { hasHScroll, scrollWidth, mirrorRef, onScroll: handleMirrorScroll },
  }
}
